import { getAccessToken } from '~/utils/authToken'
import { meansSessionExpired, sessionExpired } from '~/utils/sessionExpired'
import { apiBaseFrom } from '../../shared/apiBase'

export default defineNuxtPlugin(() => {
  const patchedGlobal = globalThis as typeof globalThis & {
    __dockAuthFetchPatched__?: boolean
    __dockAuthOriginalFetch__?: typeof globalThis.fetch
  }

  if (patchedGlobal.__dockAuthFetchPatched__) {
    return
  }

  const runtimeConfig = useRuntimeConfig()
  const apiBase = apiBaseFrom(runtimeConfig.public.apiBase)
  const originalFetch = (patchedGlobal.__dockAuthOriginalFetch__ ?? globalThis.fetch.bind(globalThis))
  patchedGlobal.__dockAuthOriginalFetch__ = originalFetch
  patchedGlobal.__dockAuthFetchPatched__ = true

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const requestUrl
      = typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input instanceof Request
            ? input.url
            : String(input)

    const isApiRequest = isApiRequestUrl(requestUrl, apiBase)
    if (!isApiRequest) {
      return originalFetch(input, init)
    }

    const token = getAccessToken()
    if (!token) {
      return originalFetch(input, init)
    }

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    )
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    const response = await originalFetch(input, {
      ...(init ?? {}),
      credentials: init?.credentials ?? 'include',
      headers
    })
    // The one place every request passes: a refused log-in is noticed here
    // and said over the screen, whichever one asked (utils/sessionExpired.ts).
    if (meansSessionExpired(pathOf(requestUrl), response.status, true)) sessionExpired.value = true
    return response
  }
})

function pathOf(requestUrl: string): string {
  try {
    return new URL(requestUrl, window.location.origin).pathname
  } catch {
    return requestUrl
  }
}

function isApiRequestUrl(requestUrl: string, apiBase: string): boolean {
  if (!apiBase) return false

  if (apiBase.startsWith('http')) {
    if (requestUrl.startsWith(apiBase)) return true

    try {
      const parsedRequest = new URL(requestUrl, window.location.origin)
      const parsedBase = new URL(apiBase)
      if (parsedRequest.origin !== parsedBase.origin) return false
      return parsedRequest.pathname.startsWith(parsedBase.pathname)
    } catch {
      return false
    }
  }

  if (apiBase.startsWith('/')) {
    try {
      const parsed = new URL(requestUrl, window.location.origin)
      return parsed.pathname.startsWith(apiBase)
    } catch {
      return requestUrl.startsWith(apiBase)
    }
  }

  return requestUrl.startsWith(apiBase)
}
